// Ruteo sobre el fragmento de la dirección (lo que va después del #).
//
// Con el fragmento el servidor siempre entrega el mismo index.html y no hace falta
// configurar nginx para cada ruta nueva. Formato de patrón: '/profesional/:id'; los
// tramos con dos puntos se entregan como parámetros a la pantalla.

import { useMemo, useSyncExternalStore } from 'react';

export type Parametros = Record<string, string>;

export interface UbicacionActual {
  /** Ruta completa, con la consulta: '/buscar?texto=dolor%20de%20espalda'. */
  ruta: string;
  /** Solo el camino: '/buscar'. */
  camino: string;
  consulta: URLSearchParams;
}

function leerRuta(): string {
  return window.location.hash.replace(/^#/, '') || '/';
}

function separar(ruta: string): { camino: string; cadena: string } {
  const corte = ruta.indexOf('?');
  return corte === -1
    ? { camino: ruta, cadena: '' }
    : { camino: ruta.slice(0, corte), cadena: ruta.slice(corte + 1) };
}

// Cuántas veces se navegó dentro de la aplicación. Con eso volver() sabe si hay una
// pantalla anterior propia o si la persona entró directo por un enlace.
let navegacionesPropias = 0;

export function irA(ruta: string, { reemplazar = false } = {}): void {
  if (leerRuta() === ruta) return;
  if (reemplazar) {
    // replace no deja la ruta intermedia en el historial: si no, el botón de atrás
    // vuelve a la redirección y la persona queda atrapada.
    window.location.replace(`#${ruta}`);
  } else {
    navegacionesPropias += 1;
    window.location.hash = ruta;
  }
  window.scrollTo(0, 0);
}

// Vuelve a la pantalla anterior conservando lo que traía (filtros, búsqueda). Si se
// entró directo por enlace, va a la ruta de referencia para no sacar a nadie del sitio.
export function volver(porDefecto = '/inicio'): void {
  if (navegacionesPropias > 0) {
    navegacionesPropias -= 1;
    window.history.back();
    return;
  }
  irA(porDefecto);
}

function suscribir(avisar: () => void): () => void {
  window.addEventListener('hashchange', avisar);
  return () => { window.removeEventListener('hashchange', avisar); };
}

export function useRuta(): UbicacionActual {
  const ruta = useSyncExternalStore(suscribir, leerRuta);
  return useMemo(() => {
    const { camino, cadena } = separar(ruta);
    return { ruta, camino, consulta: new URLSearchParams(cadena) };
  }, [ruta]);
}

// Un enlace mal armado ('%E0') haría tirar a decodeURIComponent y dejaría la
// aplicación en blanco antes de llegar a la pantalla de error.
function decodificar(valor: string): string {
  try {
    return decodeURIComponent(valor);
  } catch {
    return valor;
  }
}

export function coincidir(patron: string, ruta: string): Parametros | null {
  const partesPatron = patron.split('/').filter(Boolean);
  const partesRuta = separar(ruta).camino.split('/').filter(Boolean);
  if (partesPatron.length !== partesRuta.length) return null;

  const parametros: Parametros = {};
  for (const [i, parte] of partesPatron.entries()) {
    const valor = partesRuta[i] ?? '';
    if (parte.startsWith(':')) {
      parametros[parte.slice(1)] = decodificar(valor);
    } else if (parte !== valor) {
      return null;
    }
  }
  return parametros;
}

export function resolver<T extends { path: string }>(
  definiciones: readonly T[],
  ruta: string,
): { definicion: T; params: Parametros } | null {
  for (const definicion of definiciones) {
    const params = coincidir(definicion.path, ruta);
    if (params) return { definicion, params };
  }
  return null;
}
